import { FC, useState } from 'react';
import { IListCheckEntry } from 'src/ts/interfaces/listCheck';
import { FraudTrendEvidenceState } from '../../hooks/useFraudTrendEvidence';
import { XrayAnalysisState } from '../../hooks/useXrayAnalysis';
import { listCheckChip } from '../../helpers/listCheckHelpers';
import {
  ListCheckDetailBody,
  ListCheckDetailHead,
  ListCheckLede,
  ListChecksDetail,
  ListCheckTabButton,
  ListCheckTabStrip,
  ListCheckTitle,
  ListCheckTitleRow
} from '../../styles';
import AddedFontsTab from './AddedFontsTab';
import FontChangesOverview from './FontChangesOverview';
import FraudTrendFooter from './FraudTrendFooter';
import HeaderBarsTab from './HeaderBarsTab';
import ListCheckChip from './ListCheckChip';
import { listCheckCopy } from './listCheckCopy';

type FontChangesTab = 'overview' | 'addedFonts' | 'headerBars';

interface FontChangesDetailProps {
  entry: IListCheckEntry;
  documentXray?: XrayAnalysisState;
  fraudTrendEvidence?: FraudTrendEvidenceState;
  pdfProvider?: string | null;
  isSubmitting: boolean;
  canAnswer: boolean;
  onFraudTrendAnswer: (edited: boolean, note?: string) => void;
  onUndo: () => void;
}

const FontChangesDetail: FC<FontChangesDetailProps> = ({
  entry,
  documentXray,
  fraudTrendEvidence,
  pdfProvider,
  isSubmitting,
  canAnswer,
  onFraudTrendAnswer,
  onUndo
}) => {
  const [tab, setTab] = useState<FontChangesTab>('overview');
  const copy = listCheckCopy.fontChanges;
  const chip = listCheckChip(entry);
  const lede = entry.outcome ? listCheckCopy.fraudTrendLede[entry.outcome] : null;

  return (
    <ListChecksDetail data-testid="list-check-detail-pane">
      <ListCheckDetailHead>
        <ListCheckTitleRow>
          <ListCheckTitle>{entry.display_name}</ListCheckTitle>
          {chip && <ListCheckChip chip={chip} />}
        </ListCheckTitleRow>
      </ListCheckDetailHead>
      <ListCheckTabStrip role="tablist">
        <ListCheckTabButton
          type="button"
          role="tab"
          id="font-changes-tab-overview"
          data-testid="font-changes-tab-overview"
          aria-selected={tab === 'overview'}
          $active={tab === 'overview'}
          onClick={() => setTab('overview')}
        >
          {copy.overviewTab}
        </ListCheckTabButton>
        <ListCheckTabButton
          type="button"
          role="tab"
          id="font-changes-tab-added-fonts"
          data-testid="font-changes-tab-added-fonts"
          aria-selected={tab === 'addedFonts'}
          $active={tab === 'addedFonts'}
          onClick={() => setTab('addedFonts')}
        >
          {copy.addedFontsTab}
        </ListCheckTabButton>
        <ListCheckTabButton
          type="button"
          role="tab"
          id="font-changes-tab-header-bars"
          data-testid="font-changes-tab-header-bars"
          aria-selected={tab === 'headerBars'}
          $active={tab === 'headerBars'}
          onClick={() => setTab('headerBars')}
        >
          {copy.headerBarsTab}
        </ListCheckTabButton>
      </ListCheckTabStrip>
      <ListCheckDetailBody data-testid="list-check-detail-body">
        {tab === 'overview' && (
          <>
            {lede && <ListCheckLede>{lede}</ListCheckLede>}
            <FontChangesOverview evidence={fraudTrendEvidence?.evidenceFor(entry.check_key)} />
          </>
        )}
        {tab === 'addedFonts' && <AddedFontsTab documentXray={documentXray} />}
        {tab === 'headerBars' && (
          <HeaderBarsTab documentXray={documentXray} pdfProvider={pdfProvider} />
        )}
      </ListCheckDetailBody>
      <FraudTrendFooter
        entry={entry}
        isSubmitting={isSubmitting}
        canAnswer={canAnswer}
        onAnswer={onFraudTrendAnswer}
        onUndo={onUndo}
      />
    </ListChecksDetail>
  );
};

export default FontChangesDetail;
